/* verqo — Buyers Homepage V3 preview · June 2026
   Exact same pattern as the seller HomeV3Preview, buyer persona:
   buyer hero screenshot, credit-line/PO-inbox/payments/shipping features.
   PREVIEW ONLY at /preview/home-v3-buyers. */
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, CheckCircle2, Banknote, ShieldCheck, Truck, Wallet,
  Sparkles, CreditCard, Radar, FileText, Zap, Inbox, Search,
  Shirt, Coffee, PawPrint, HeartPulse, Home,
} from "lucide-react";
import { PreviewNavHeader } from "../../components/marketing/PreviewNavHeader";
import { PreviewFooter } from "../../components/marketing/PreviewFooter";
import { Reveal } from "../../components/marketing/SubPageBlocks";
import PageSEO from "../../components/marketing/PageSEO";

const PAPER = "#f7f6f2";

const Hi = ({ children }) => (
  <span className="relative inline-block px-2 -rotate-1 bg-lime-300 rounded-sm">{children}</span>
);

function PanShot({ src, alt, ratio = "16 / 10", duration = 18, zoom = 1, fill = false }) {
  return (
    <div className={`relative overflow-hidden ${fill ? "h-full w-full" : ""}`} style={fill ? undefined : { aspectRatio: ratio }}>
      <div className="absolute inset-0 v3-ken" style={{ animationDuration: `${duration}s` }}>
        <img
          src={src} alt={alt} loading="lazy"
          className="w-full h-full object-cover object-top"
          style={{ transform: `scale(${zoom})`, transformOrigin: "top center" }}
        />
      </div>
    </div>
  );
}

function FrameShot({ src, url, alt, ratio = "16 / 10", duration = 22 }) {
  return (
    <div className="relative rounded-xl overflow-hidden border border-slate-200 bg-white shadow-[0_28px_80px_-24px_rgba(15,23,42,0.24)]">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-200 bg-slate-50">
        <div className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full bg-red-300"></span>
          <span className="w-2.5 h-2.5 rounded-full bg-amber-300"></span>
          <span className="w-2.5 h-2.5 rounded-full bg-emerald-300"></span>
        </div>
        {url && <div className="ml-3 flex-1 rounded-md bg-white border border-slate-200 px-2.5 py-0.5 text-[10.5px] text-slate-500 font-mono truncate">{url}</div>}
      </div>
      <PanShot src={src} alt={alt} ratio={ratio} duration={duration} />
    </div>
  );
}

function FloatChip({ icon: Icon, title, sub, className, delay = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
      className={`absolute hidden lg:flex items-center gap-2.5 rounded-xl border border-slate-200 bg-white/95 backdrop-blur px-3.5 py-2.5 shadow-[0_18px_40px_-16px_rgba(15,23,42,0.28)] ${className}`}
    >
      <div className="w-8 h-8 rounded-lg bg-lime-100 grid place-items-center flex-shrink-0">
        <Icon className="w-4 h-4 text-lime-700" strokeWidth={2} />
      </div>
      <div>
        <div className="text-[12px] font-semibold text-slate-900 leading-tight">{title}</div>
        <div className="text-[10.5px] text-slate-500 leading-tight mt-0.5">{sub}</div>
      </div>
      <CheckCircle2 className="w-3.5 h-3.5 text-lime-600 flex-shrink-0" />
    </motion.div>
  );
}

function BentoCard({ icon: Icon, title, body, img, to, big = false }) {
  return (
    <Link
      to={to}
      className={`group relative flex flex-col rounded-2xl border border-slate-200 bg-white overflow-hidden hover:border-lime-400 hover:shadow-[0_24px_60px_-24px_rgba(15,23,42,0.22)] transition-all ${big ? "lg:col-span-2" : ""}`}
      data-testid={`bento-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
    >
      <div className="p-6 md:p-7 pb-0">
        <div className="flex items-center gap-2.5 mb-3">
          <div className="w-8 h-8 rounded-lg bg-lime-100 grid place-items-center">
            <Icon className="w-4 h-4 text-lime-700" strokeWidth={1.8} />
          </div>
          <h3 className="text-[17px] font-semibold tracking-tight text-slate-900">{title}</h3>
        </div>
        <p className="text-[13px] leading-relaxed text-slate-600 max-w-md">{body}</p>
      </div>
      <div className="mt-5 px-6 md:px-7 flex-1">
        <div className="rounded-t-lg border border-b-0 border-slate-200 overflow-hidden shadow-sm h-full min-h-[210px]">
          <PanShot fill src={img} alt={title} zoom={big ? 1.12 : 1.45} duration={16} />
        </div>
      </div>
    </Link>
  );
}

function DeepDive({ eyebrow, title, body, bullets, img, url, to, flip = false, icon: Icon }) {
  return (
    <div className={`grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center ${flip ? "lg:[&>*:first-child]:order-2" : ""}`}>
      <Reveal>
        <div>
          <div className="inline-flex items-center gap-2 text-[10.5px] uppercase tracking-[0.26em] text-lime-700 font-semibold">
            <Icon className="w-3.5 h-3.5" /> {eyebrow}
          </div>
          <h3 className="mt-4 text-[28px] md:text-[38px] font-semibold tracking-tight leading-[1.05] text-slate-900">{title}</h3>
          <p className="mt-4 text-[15px] leading-relaxed text-slate-600 max-w-lg">{body}</p>
          <div className="mt-6 space-y-2.5">
            {bullets.map((b) => (
              <div key={b} className="flex items-start gap-2.5 text-[13.5px] text-slate-700">
                <CheckCircle2 className="w-4 h-4 text-lime-600 mt-0.5 flex-shrink-0" /> {b}
              </div>
            ))}
          </div>
          <Link to={to} className="mt-7 inline-flex items-center gap-1.5 text-[13.5px] font-semibold text-slate-900 hover:text-lime-700 transition-colors group">
            Explore <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </Reveal>
      <Reveal delay={0.12} y={30}>
        <FrameShot src={img} url={url} alt={title} duration={22} />
      </Reveal>
    </div>
  );
}

const CATEGORIES = [
  { name: "Beauty", icon: Sparkles },
  { name: "Fashion & Apparel", icon: Shirt },
  { name: "Food & Beverage", icon: Coffee },
  { name: "Pet", icon: PawPrint },
  { name: "Health & Wellness", icon: HeartPulse },
  { name: "Household CPG", icon: Home },
];

export default function HomeV3BuyersPreview() {
  return (
    <div className="home-v3 min-h-screen text-slate-900" style={{ ["--brand-fg"]: "#0c1612", ["--brand-accent"]: "#dcff3a", background: PAPER }}>
      <style>{`
        .home-v3 header { background: rgba(247,246,242,0.88) !important; border-bottom-color: rgba(15,23,42,0.06) !important; }
        @keyframes v3ken {
          0%, 100% { transform: scale(1) translateY(0); }
          50%      { transform: scale(1.09) translateY(-2.5%); }
        }
        .v3-ken { animation-name: v3ken; animation-timing-function: ease-in-out; animation-iteration-count: infinite; transform-origin: top center; will-change: transform; }
      `}</style>
      <PageSEO title="verqo for buyers · Buy wholesale on your terms" description="Everything retailers need to source, order, and pay later — one credit line, PO inbox, payments, and shipping in one platform." path="/buyers" />
      <PreviewNavHeader modeOverride="buyer" />

      {/* ═══ HERO ═══ */}
      <section className="relative pt-20 md:pt-28 pb-0 px-6 overflow-hidden" data-testid="v3b-hero">
        <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse 70% 50% at 50% 0%, rgba(220,255,58,0.16) 0%, transparent 60%)" }} />
        <div className="relative max-w-[780px] mx-auto text-center">
          <Reveal>
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-lime-300 bg-lime-50 text-[11px] font-semibold tracking-wide text-lime-800" data-testid="v3b-hero-kicker">
              <Sparkles className="w-3 h-3" /> Banking built in*
            </div>
          </Reveal>
          <Reveal delay={0.08}>
            <h1 className="mt-6 text-[38px] sm:text-[50px] md:text-[60px] leading-[1.04] tracking-tight font-semibold text-slate-900" data-testid="v3b-hero-headline">
              Buy wholesale<br className="hidden md:block" /> on <Hi>your terms</Hi>.
            </h1>
          </Reveal>
          <Reveal delay={0.18}>
            <p className="mt-6 mx-auto max-w-xl text-[16px] md:text-[17px] leading-relaxed text-slate-600" data-testid="v3b-hero-sub">
              Everything retailers need to <strong className="text-slate-900">source, order, and pay later</strong> —
              one credit line, PO inbox, payments, and shipping in one platform.
            </p>
          </Reveal>
          <Reveal delay={0.28}>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <a href="/waitlist?as=buyer" data-testid="v3b-cta-primary" className="group inline-flex items-center gap-2 px-7 py-3.5 rounded-lg text-white text-sm font-semibold hover:opacity-90 transition-opacity" style={{ background: "var(--brand-fg)" }}>
                Get your credit line <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </a>
              <Link to="/buyers/pricing" data-testid="v3b-cta-secondary" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-lg border border-slate-300 bg-white/60 text-slate-900 text-sm font-medium hover:bg-white transition-colors">
                See pricing
              </Link>
            </div>
          </Reveal>
          <Reveal delay={0.36}>
            <div className="mt-6 flex flex-wrap justify-center items-center gap-x-6 gap-y-2 text-[12px] text-slate-500">
              <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-lime-600" /> Net-30/60/90 across every supplier</span>
              <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-lime-600" /> Approved in under 60 seconds</span>
              <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-lime-600" /> One application, whole network</span>
            </div>
          </Reveal>
        </div>

        {/* BIG buyer dashboard */}
        <div className="relative max-w-[1240px] mx-auto mt-12 md:mt-16" data-testid="v3b-hero-screenshot">
          <FloatChip icon={CreditCard} title="Terms approved · Net-60" sub="$120,000 credit line active" className="-left-5 xl:-left-9 bottom-44 z-10" delay={0.5} />
          <FloatChip icon={Inbox} title="PO auto-matched" sub="PO #7311 · invoice reconciled" className="-right-5 xl:-right-9 top-36 z-10" delay={0.65} />
          <FloatChip icon={Banknote} title="Payment scheduled" sub="Auto-debit · due in 42 days" className="-left-5 xl:-left-9 bottom-16 z-10" delay={0.8} />
          <FloatChip icon={Truck} title="Shipment in transit" sub="ATL → CHI · ETA Fri" className="-right-5 xl:-right-9 bottom-28 z-10" delay={0.95} />
          <Reveal delay={0.3} y={40}>
            <div className="rounded-2xl overflow-hidden ring-1 ring-slate-900/10 shadow-[0_60px_140px_-40px_rgba(15,23,42,0.38)] bg-white">
              <img src="/preview-screens/buyer/dashboard-hero.jpeg" alt="Verqo buyer dashboard" className="block w-full h-auto" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* ═══ CATEGORIES STRIP ═══ */}
      <section className="border-y border-slate-900/[0.06] bg-white/50 mt-14 md:mt-20" data-testid="v3b-categories-strip">
        <div className="max-w-[1140px] mx-auto px-6 py-10 md:py-12 text-center">
          <div className="text-[10.5px] uppercase tracking-[0.28em] text-lime-700 font-semibold">Built for these categories</div>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-2.5 md:gap-3">
            {CATEGORIES.map((c) => (
              <div key={c.name} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-slate-200 bg-white hover:border-lime-400 hover:-translate-y-0.5 transition-all"
                data-testid={`v3b-category-${c.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <c.icon className="w-3.5 h-3.5 text-slate-600" strokeWidth={1.8} />
                <span className="text-[12.5px] font-medium text-slate-700">{c.name}</span>
              </div>
            ))}
          </div>
          <p className="mt-6 text-[13px] text-slate-500" data-testid="v3b-icp-line">
            For retailers, chains &amp; online sellers — <span className="font-semibold text-slate-700">one credit line</span> across every supplier.
          </p>
        </div>
      </section>

      {/* ═══ BENTO ═══ */}
      <section className="max-w-[1140px] mx-auto px-6 pt-20 md:pt-28 pb-8" data-testid="v3b-bento">
        <Reveal>
          <div className="max-w-2xl">
            <div className="text-[10.5px] uppercase tracking-[0.28em] text-lime-700 font-semibold">One source of truth</div>
            <h2 className="mt-4 text-[30px] md:text-[44px] font-semibold tracking-tight leading-[1.04] text-slate-900">
              Every order, every supplier, one ledger.
            </h2>
            <p className="mt-4 text-[15px] leading-relaxed text-slate-600">
              POs, invoices, payments, and shipments from all your suppliers run through the same account —
              nothing re-keyed, exported, or matched by hand again.
            </p>
          </div>
        </Reveal>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          <BentoCard big icon={CreditCard} title="One credit line, every supplier" body="Apply once, get Net-30/60/90 everywhere you buy. No re-applying, no paper trade references, no fax-back forms." img="/preview-screens/buyer/capital.png" to="/buyers/product/credit-terms" />
          <BentoCard icon={Inbox} title="PO inbox on autopilot" body="Every PO tracked, acknowledged, and matched to its invoice — from any supplier, on or off Verqo." img="/preview-screens/buyer-po-inbox/main.jpeg" to="/buyers/product/procurement" />
          <BentoCard icon={Wallet} title="Pay every supplier in one place" body="One statement, auto-debit on due dates, and split-pay when cash is tight." img="/preview-screens/buyer/payments.png" to="/buyers/product/pay" />
          <BentoCard big icon={Truck} title="Track every shipment live" body="Parcel and freight from every supplier on one live map — rates, labels, BOLs, and claims included." img="/preview-screens/buyer-shipping/main.jpeg" to="/buyers/product/ship" />
        </div>
      </section>

      {/* ═══ DEEP DIVES ═══ */}
      <section className="max-w-[1140px] mx-auto px-6 pt-16 md:pt-24 pb-8 space-y-24 md:space-y-32" data-testid="v3b-deepdives">
        <DeepDive
          icon={Search} eyebrow="Source"
          title="Shop every supplier's catalog in one place."
          body="Browse live wholesale catalogs, compare pricing, and reorder in two clicks — from the brands you already buy and new ones you discover on Verqo."
          bullets={["Live catalogs with your negotiated pricing", "Two-click reorders from purchase history", "RFQs to any supplier, on or off Verqo"]}
          img="/preview-screens/buyer/catalog.png" url="app.verqo.com/buyer/catalog"
          to="/buyers/product/discover"
        />
        <DeepDive flip
          icon={Radar} eyebrow="Pay on terms"
          title="Net-30/60/90, approved in 60 seconds."
          body="Verqo underwrites you once and extends terms at every supplier on the network. Your trade score travels with you and grows your limit as you pay on time."
          bullets={["One application — 60-second decision", "Trade score that unlocks bigger limits", "Auto-debit on due dates, zero late fees surprises"]}
          img="/preview-screens/buyer/score.png" url="app.verqo.com/buyer/score"
          to="/buyers/product/trade-score"
        />
        <DeepDive
          icon={Truck} eyebrow="Track"
          title="Every order and shipment, one live view."
          body="See every PO's status from confirmation to delivery — with live GPS tracking, documents, and claims handled inside the same platform."
          bullets={["Live order pipeline across all suppliers", "GPS tracking + documents (BOL, POD, invoice)", "File claims without leaving the platform"]}
          img="/preview-screens/buyer/orders.png" url="app.verqo.com/buyer/orders"
          to="/buyers/product/ship"
        />
      </section>

      {/* ═══ HOW IT WORKS ═══ */}
      <section className="max-w-[1140px] mx-auto px-6 pt-16 md:pt-24 pb-10" data-testid="v3b-how">
        <Reveal>
          <div className="text-center max-w-2xl mx-auto">
            <div className="text-[10.5px] uppercase tracking-[0.28em] text-lime-700 font-semibold">How it works</div>
            <h2 className="mt-4 text-[28px] md:text-[40px] font-semibold tracking-tight leading-[1.05] text-slate-900">Terms at your first supplier in minutes.</h2>
          </div>
        </Reveal>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5">
          {[
            [Zap, "01 · Apply once", "A 60-second application underwrites you for the whole network — no paper trade references."],
            [FileText, "02 · Order anywhere", "Check out with Net-30/60/90 at any Verqo supplier. Your terms follow you."],
            [Banknote, "03 · Pay later, in one place", "One statement across all suppliers. Auto-debit on due dates, split-pay when you need it."],
          ].map(([Icon, h, b], i) => (
            <Reveal key={h} delay={i * 0.08}>
              <div className="rounded-2xl border border-slate-200 bg-white p-7 h-full hover:border-lime-400 transition-colors">
                <div className="w-9 h-9 rounded-lg bg-lime-100 grid place-items-center mb-5"><Icon className="w-4.5 h-4.5 text-lime-700" strokeWidth={1.8} /></div>
                <div className="text-[15px] font-semibold text-slate-900">{h}</div>
                <p className="mt-2 text-[13px] leading-relaxed text-slate-600">{b}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ═══ QUOTE ═══ */}
      <section className="max-w-[820px] mx-auto px-6 pt-14 md:pt-20 pb-8 text-center" data-testid="v3b-quote">
        <Reveal>
          <div className="text-lime-500 text-[44px] leading-none font-serif">“</div>
          <blockquote className="text-[20px] md:text-[26px] font-medium tracking-tight leading-snug text-slate-900">
            We stopped filling out credit applications at every new brand.
            One Verqo line covers all our suppliers — and the terms grew as we paid on time.
          </blockquote>
          <div className="mt-5 text-[13px] text-slate-500">Owner · 3-door specialty retail chain</div>
        </Reveal>
      </section>

      {/* ═══ DARK CTA ═══ */}
      <section className="max-w-[1140px] mx-auto px-6 pt-14 md:pt-20 pb-24" data-testid="v3b-cta-banner">
        <Reveal>
          <div className="relative rounded-3xl overflow-hidden px-8 md:px-16 py-14 md:py-20 text-center" style={{ background: "var(--brand-fg)" }}>
            <div className="absolute inset-0 opacity-[0.05] pointer-events-none" style={{ backgroundImage: "linear-gradient(rgba(220,255,58,1) 1px, transparent 1px), linear-gradient(90deg, rgba(220,255,58,1) 1px, transparent 1px)", backgroundSize: "30px 30px" }} />
            <div className="relative">
              <h2 className="text-[30px] md:text-[44px] font-semibold tracking-tight leading-[1.05] text-white">
                Ready to buy wholesale <span className="text-lime-300">on your terms?</span>
              </h2>
              <p className="mt-4 text-[15px] text-white/60 max-w-xl mx-auto">One application, one credit line, every supplier — approved in under 60 seconds.</p>
              <div className="mt-8 flex flex-wrap justify-center gap-3">
                <a href="/waitlist?as=buyer" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-lg bg-lime-300 text-slate-900 text-sm font-semibold hover:bg-lime-200 transition-colors" data-testid="v3b-cta-banner-btn">
                  Get your credit line <ArrowRight className="w-4 h-4" />
                </a>
                <Link to="/buyers/pricing" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-lg border border-white/25 text-white text-sm font-medium hover:bg-white/10 transition-colors">See pricing</Link>
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      {/* Compliance footnote — bottom of page */}
      <div className="max-w-[1140px] mx-auto px-6 pb-10 -mt-12">
        <p className="text-center text-[10.5px] leading-relaxed text-slate-400 max-w-2xl mx-auto" data-testid="v3b-fdic-note">
          *Verqo is a financial technology company, not a bank. Banking services are provided by our partner banks, Members FDIC.
        </p>
      </div>

      <PreviewFooter modeOverride="buyer" />
    </div>
  );
}
